import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, Send, Loader2 } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useProducts } from "@/context/ProductsContext";
import { useMedia } from "@/context/MediaContext";
import { productImage } from "@/components/ProductCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/concierge`;
const MODELS = [{ key: "claude", label: "Claude" }, { key: "gpt", label: "ChatGPT" }];
const STARTERS = ["Help me pick a scent for a date night", "Something fresh for the office", "What lasts longest?", "A gift for her under ₹2,000"];
const EASE = [0.22, 1, 0.36, 1];

const getSession = () => {
  let s = localStorage.getItem("r30_concierge_session");
  if (!s) {
    s = `s-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
    localStorage.setItem("r30_concierge_session", s);
  }
  return s;
};

const ProductChip = ({ slug, onClose }) => {
  const { products } = useProducts();
  const { addItem } = useCart();
  const img = useMedia();
  const p = products.find((x) => x.slug === slug);
  if (!p) return null;
  return (
    <div data-testid={`concierge-product-${slug}`} className="flex items-center gap-3 border border-black/10 bg-white/60 p-2 pr-3">
      <img src={productImage(p, img)} alt={p.name} className="h-14 w-11 object-cover bg-[#EFE9E1]" />
      <div className="flex-1 min-w-0">
        <p className="font-serif-brand text-base leading-tight truncate">{p.name}</p>
        <p className="text-[0.6rem] uppercase tracking-[0.18em] text-[#BA8D53] truncate">{(p.families || []).join(" · ")}</p>
        <Link to={`/products/${p.slug}`} onClick={onClose} className="text-[0.6rem] uppercase tracking-[0.18em] link-line">View</Link>
      </div>
      {p.available !== false && (
        <button type="button" onClick={() => { addItem(p.slug, "50ml"); onClose(); }} data-testid={`concierge-add-${slug}`} className="text-[0.58rem] uppercase tracking-[0.18em] border border-[#111] px-2.5 py-2 hover:bg-[#111] hover:text-[#F7F3EE] transition-colors">
          Add ₹{Math.round(p.price50).toLocaleString("en-IN")}
        </button>
      )}
    </div>
  );
};

const Bubble = ({ m, onClose }) => (
  <div className={`flex flex-col gap-2 ${m.role === "user" ? "items-end" : "items-start"}`} data-testid={`concierge-msg-${m.role}`}>
    <div className={`max-w-[88%] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-[#111] text-[#F7F3EE]" : "bg-white/70 border border-black/10"}`}>
      {m.content || <Loader2 size={14} className="animate-spin text-[#BA8D53]" />}
    </div>
    {m.products?.length > 0 && <div className="w-full max-w-[88%] space-y-2">{m.products.map((s) => <ProductChip key={s} slug={s} onClose={onClose} />)}</div>}
  </div>
);

export default function ConciergeChat() {
  const [open, setOpen] = useState(false);
  const [model, setModel] = useState(() => localStorage.getItem("r30_concierge_model") || "claude");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const session = useMemo(getSession, []);
  const endRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { localStorage.setItem("r30_concierge_model", model); }, [model]);
  useEffect(() => {
    if (!open || messages.length) return;
    fetch(`${API}/history/${session}`).then((r) => r.json()).then((h) => Array.isArray(h) && setMessages(h.map((m) => ({ role: m.role, content: m.content, products: m.products || [] })))).catch(() => {});
  }, [open, session, messages.length]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);
  useEffect(() => {
    document.body.style.overflow = open && window.innerWidth < 1024 ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "", products: [] }]);
    try {
      const res = await fetch(`${API}/chat`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ session_id: session, message: msg, model }) });
      if (!res.ok || !res.body) throw new Error("bad response");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const ev = JSON.parse(part.slice(6));
          setMessages((m) => {
            const next = [...m];
            const last = { ...next[next.length - 1] };
            if (ev.delta) last.content += ev.delta;
            if (ev.error) last.content = ev.error;
            if (ev.products) last.products = ev.products;
            next[next.length - 1] = last;
            return next;
          });
        }
      }
    } catch {
      setMessages((m) => { const next = [...m]; next[next.length - 1] = { role: "assistant", content: "The concierge is momentarily unavailable. Please try again." }; return next; });
    } finally {
      setBusy(false);
      inputRef.current?.focus();
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="concierge-open-button"
        aria-label="Open fragrance concierge"
        className={`fixed z-40 right-4 bottom-20 lg:right-6 lg:bottom-6 h-13 w-13 p-3.5 rounded-full bg-[#111] text-[#BA8D53] shadow-lg hover:scale-110 transition-transform duration-300 flex items-center justify-center ${open ? "hidden" : ""}`}
      >
        <Sparkles size={22} strokeWidth={1.5} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.aside
            data-testid="concierge-panel"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="fixed z-[60] inset-0 lg:inset-auto lg:right-6 lg:bottom-6 lg:w-[400px] lg:h-[640px] lg:max-h-[calc(100vh-3rem)] bg-[#F7F3EE] lg:border border-black/10 lg:shadow-2xl flex flex-col"
          >
            <header className="flex items-center justify-between gap-3 px-4 py-3 border-b border-black/10 bg-[#EFE9E1]">
              <div>
                <p className="text-[0.58rem] uppercase tracking-[0.26em] text-[#BA8D53]">Reserve 30</p>
                <h2 className="font-serif-brand text-xl font-light leading-tight">Fragrance Concierge</h2>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex border border-black/15 text-[0.58rem] uppercase tracking-[0.16em]" data-testid="concierge-model-toggle">
                  {MODELS.map((m) => (
                    <button key={m.key} type="button" onClick={() => setModel(m.key)} data-testid={`concierge-model-${m.key}`} className={`px-2.5 py-2 transition-colors ${model === m.key ? "bg-[#111] text-[#F7F3EE]" : "text-black/55 hover:text-black"}`}>
                      {m.label}
                    </button>
                  ))}
                </div>
                <button type="button" onClick={() => setOpen(false)} data-testid="concierge-close-button" aria-label="Close" className="h-10 w-10 flex items-center justify-center hover:bg-black/5 transition-colors">
                  <X size={18} strokeWidth={1.5} />
                </button>
              </div>
            </header>

            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4" data-testid="concierge-messages">
              {messages.length === 0 && (
                <div className="space-y-4">
                  <div className="bg-white/70 border border-black/10 px-4 py-3 text-sm leading-relaxed">
                    Good day. I'm your fragrance concierge — tell me the occasion, the mood or a note you love, and I'll find your Reserve 30 match.
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {STARTERS.map((s) => (
                      <button key={s} type="button" onClick={() => send(s)} data-testid="concierge-starter" className="text-xs border border-black/15 px-3 py-2 hover:border-[#BA8D53] hover:text-[#BA8D53] transition-colors text-left">
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, i) => <Bubble key={i} m={m} onClose={() => setOpen(false)} />)}
              <div ref={endRef} />
            </div>

            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-black/10 p-3 flex items-center gap-2 bg-[#F7F3EE] pb-[max(0.75rem,env(safe-area-inset-bottom))]">
              <input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                data-testid="concierge-input"
                placeholder="Ask about notes, longevity, gifting…"
                maxLength={1000}
                className="flex-1 h-12 bg-white/70 border border-black/15 px-4 text-sm focus:outline-none focus:border-[#BA8D53]"
              />
              <button type="submit" disabled={busy || !input.trim()} data-testid="concierge-send-button" aria-label="Send" className="h-12 w-12 flex items-center justify-center bg-[#111] text-[#F7F3EE] disabled:opacity-40 hover:bg-[#BA8D53] transition-colors">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} strokeWidth={1.5} />}
              </button>
            </form>
          </motion.aside>
        )}
      </AnimatePresence>
    </>
  );
}
